import type { MessageRouter } from "@pdfking/functionpool"
import type { Page } from "@pdfking/renderer"
import type {
  AnyGUID,
  AsyncSequence,
  Partial,
  StringGUID,
} from "@pdfking/tfunc"
import {
  ArrayToIterable,
  BlobToUint8,
  First,
  GuidToString,
  MapAsync,
  MethodDispatcher,
  StreamCreate,
  UUID,
} from "@pdfking/tfunc"
import { AtomHash } from "@pdfking/tfunc"

import { CreateRemoteFile } from "../files/CreateRemoteFile"
import { GenerateFile } from "../files/GenerateFile"
import { RequestBlob } from "../files/RequestBlob"
import { FileContentGUID } from "../methods/FileContentGUID"
import type { APIRequest } from "../types/api/APIRequest"
import type { APIResponse } from "../types/api/APIResponse"
import type { PdfEngine } from "../types/engine/PdfEngine"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import type { GeneratedFile } from "../types/files/cached/GeneratedFile"
import type { RemoteFile } from "../types/files/cached/RemoteFile"
import type { SinglePageImage } from "../types/files/cached/SinglePageImage"
import type { SinglePagePdf } from "../types/files/cached/SinglePagePdf"
import type { VirtualFile } from "../types/files/VirtualFile"
import { VirtualFileType } from "../types/files/VirtualFileType"
import type { TaskResult } from "../types/tasks/input/TaskResult"
import type { FilePlottingContext } from "../types/tasks/options/TaskPlotOptions"
import { TaskName } from "../types/tasks/TaskName"
import type { TaskOptions } from "../types/tasks/TaskOptions"
import { CreatePdfEngineDefaults } from "./CreatePdfEngineDefaults"

const make_task_id = (t: TaskName, ...args: AnyGUID[]): Promise<StringGUID> =>
  AtomHash([t, ...args]).then(GuidToString)

// Top-level message queue router with post-processing
function ForwardTask(
  router: MessageRouter<APIRequest, APIResponse>,
  options: TaskOptions,
  ...input: AsyncSequence<APIRequest>[]
): AsyncGenerator<VirtualFile> {
  const responses = router(ArrayToIterable([options], ...input))
  return MapAsync(response => (response as TaskResult).content, responses)
}

export function CreateEngineVirtual(
  config: Partial<PdfEngineOptions>,
  router: MessageRouter<APIRequest, APIResponse>
): PdfEngine {
  const options = CreatePdfEngineDefaults(config)

  async function* mapSinglePage<T>(
    processor: (page: SinglePagePdf) => AsyncSequence<T>,
    files: AsyncSequence<VirtualFile>
  ): AsyncGenerator<T> {
    for await (const file of files) {
      for await (const page of SplitPdf(file)) {
        yield* processor(page)
      }
    }
  }

  async function* innerSplit(
    source: GeneratedFile | RemoteFile
  ): AsyncGenerator<SinglePagePdf> {
    yield* ForwardTask(router, {
      options,
      enum: TaskName.TASK_SPLIT,
      task_id: await make_task_id(TaskName.TASK_SPLIT, FileContentGUID(source)),
      source,
    }) as AsyncGenerator<SinglePagePdf>
  }

  const SplitPdf = MethodDispatcher<
    [VirtualFile],
    AsyncGenerator<SinglePagePdf>
  >({
    [VirtualFileType.SINGLE_PAGE_IMAGE]: p => ArrayToIterable([p.source]),
    [VirtualFileType.SINGLE_PAGE_PDF]: p => ArrayToIterable([p]),
    [VirtualFileType.MULTI_PAGE_PDF]: p => ArrayToIterable(p.pages),
    [VirtualFileType.ZIP_FILE]: p =>
      mapSinglePage(p => [p], Object.values(p.files)),
    [VirtualFileType.GENERATED_FILE]: innerSplit,
    [VirtualFileType.REMOTE_FILE]: innerSplit,
  })

  async function* rasterizeSingle(
    source: SinglePagePdf
  ): AsyncGenerator<SinglePageImage> {
    yield* ForwardTask(router, {
      options,
      enum: TaskName.TASK_RASTERIZE,
      task_id: await make_task_id(
        TaskName.TASK_RASTERIZE,
        FileContentGUID(source)
      ),
      source,
    }) as AsyncGenerator<SinglePageImage>
  }

  const RasterizePdf = MethodDispatcher<
    [VirtualFile],
    AsyncGenerator<SinglePageImage>
  >(
    {
      [VirtualFileType.SINGLE_PAGE_IMAGE]: p => ArrayToIterable([p]),
      [VirtualFileType.SINGLE_PAGE_PDF]: rasterizeSingle,
      [VirtualFileType.MULTI_PAGE_PDF]: p =>
        mapSinglePage(rasterizeSingle, p.pages),
    },
    p => mapSinglePage(rasterizeSingle, [p])
  )

  async function MergePdf(
    files: AsyncSequence<[number, number, SinglePagePdf] | VirtualFile>,
    task_id: StringGUID | undefined = undefined
  ): Promise<GeneratedFile> {
    let page_index = 0

    const pages = async function* (): AsyncGenerator<
      [number, number, SinglePagePdf]
    > {
      for await (const page of files) {
        if (Array.isArray(page)) {
          yield page as [number, number, SinglePagePdf]
          page_index = Math.max(page_index, page[1])
        } else {
          const page_count = page_index
          for await (const pdf of SplitPdf(page)) {
            yield [++page_index, page_count + pdf.page_count, pdf]
          }
        }
      }
    }

    return First(
      ForwardTask(
        router,
        {
          options,
          enum: TaskName.TASK_MERGE,
          task_id: await make_task_id(TaskName.TASK_MERGE, task_id ?? UUID()),
        },
        MapAsync(
          ([page_number, page_count, file]) => ({
            enum: TaskName.CONTENT_PAGED,
            page_number,
            page_count,
            content: file,
          }),
          pages()
        )
      )
    ) as Promise<GeneratedFile>
  }

  async function ZipFiles(
    files: AsyncSequence<[string, VirtualFile]>,
    task_id: StringGUID | undefined = undefined
  ): Promise<GeneratedFile> {
    return First(
      ForwardTask(
        router,
        {
          options,
          enum: TaskName.TASK_ZIP,
          task_id: await make_task_id(TaskName.TASK_ZIP, task_id ?? UUID()),
        },
        MapAsync(
          ([name, file]) => ({
            enum: TaskName.CONTENT_FILE,
            content: file,
            name,
          }),
          files
        )
      )
    ) as Promise<GeneratedFile>
  }

  async function PlotPdf(
    page: Page,
    context: FilePlottingContext = {},
    task_id: StringGUID | undefined = undefined
  ): Promise<SinglePagePdf> {
    return First(
      ForwardTask(router, {
        options,
        enum: TaskName.TASK_PLOT,
        page,
        context,
        task_id: task_id || GuidToString(UUID()),
      })
    ) as Promise<SinglePagePdf>
  }

  const engine: PdfEngine = {
    config: options,
    SplitPdf,
    RasterizePdf,
    MergePdf,
    ZipFiles,
    PlotPdf,
    CreateRemoteFile,
    GenerateFile: (stream, content_type) =>
      GenerateFile(engine, stream, content_type),
    RequestContent: (file, emit) =>
      RequestBlob(engine, file, emit).then(
        ({ chunks, size }) =>
          new Response(StreamCreate(MapAsync(BlobToUint8, chunks)), {
            headers: {
              "content-length": size.toString(),
            },
          })
      ),
  }

  return engine
}
